import {
  Button,
  Carousel,
  Lightbox,
  Pill,
  Tile,
  TileContent,
  TileMedia,
  TileText,
  TileTitle,
} from '@design-system';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { getArtistTileOverline } from '@utils/artists';
import { getWordCount } from '@utils/journals/franchises';
import { storyblokMediaItemToLightboxEntry } from '@utils/lightbox';
import { readingTimeCalculator } from '@utils/readingTimeCalculator';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import Link from 'next/link';
import {
  ComponentProps,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { AuthEntryPoint } from '../../features/auth/types';
import type { AugmentedArtistCard } from '../../types/Artists';
import {
  AssetStoryblok,
  EstatesStoryblok,
  MultiassetStoryblok,
  VideoStoryblok,
} from '../../types/generated-storyblok';
import { Image } from '../Image';
import { JournalsTile } from '../Journals';
import PageSection from '../Layout/PageSection';
import { SubscriptionBanner } from '../Subscriptions';
import VideoCard from '../VideoCard';

const STYLE_H1 = {
  fontSize: 'clamp(1.875rem, 3.348vw + 0.536rem, 3.75rem)',
  lineHeight: 'clamp(2.25rem, 2.679vw + 1.179rem, 3.75rem)',
  letterSpacing: '-2%',
};

const STYLE_H2 = {
  fontSize: 'clamp(1.875rem, 1.116vw + 1.429rem, 2.5rem)',
  lineHeight: 'clamp(2.25rem, 1.339vw + 1.714rem, 3rem)',
  letterSpacing: '-2%',
};

const STYLE_QUOTE = {
  fontSize: 'clamp(1.25rem, 2.232vw + 0.357rem, 2.5rem)',
  lineHeight: 'clamp(1.5rem, 2.679vw + 0.429rem, 3rem)',
  letterSpacing: '-1%',
};

const ShuffleImage = ({
  delay,
  imageIdx,
  images,
  interval,
  onCycle,
  zoom,
}: {
  images: MultiassetStoryblok;
  imageIdx: number;
  delay: number;
  interval: number;
  onCycle: () => void;
  zoom?: 'in' | 'out';
}) => {
  const onCycleRef = useRef(onCycle);
  onCycleRef.current = onCycle;

  useEffect(() => {
    let timerId: ReturnType<typeof setInterval>;

    const timeoutId = setTimeout(() => {
      timerId = setInterval(() => {
        onCycleRef.current();
      }, interval);
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(timerId);
    };
  }, []);

  const scaleFrom = zoom === 'out' ? 1.2 : 1;
  const scaleTo = zoom === 'in' ? 1.2 : 1;
  const currentImage = images[imageIdx];

  if (!currentImage) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={imageIdx}
        className="absolute inset-0"
        initial={{ opacity: 0, scale: scaleFrom }}
        animate={{ opacity: 1, scale: scaleTo }}
        exit={{ opacity: 0 }}
        transition={{
          opacity: { duration: 0.6, ease: 'easeInOut' },
          scale: { duration: interval / 1000, ease: 'linear' },
        }}
      >
        <Image
          className="h-full w-full"
          ignoreAspectRatio
          alt={currentImage.alt ?? ''}
          src={currentImage.filename}
        />
      </motion.div>
    </AnimatePresence>
  );
};

const HERO_NUM_SLOTS = 13;

type HeroImageState = { indices: number[]; queue: number[] };

const HeroFloatingImages = ({ images }: { images: MultiassetStoryblok }) => {
  const [heroState, setHeroState] = useState<HeroImageState>(() => ({
    indices: Array.from(
      { length: HERO_NUM_SLOTS },
      (_, i) => i % images.length,
    ),
    queue: Array.from(
      { length: Math.max(0, images.length - HERO_NUM_SLOTS) },
      (_, i) => i + HERO_NUM_SLOTS,
    ),
  }));

  const cycleSlot = useCallback((slotIdx: number) => {
    setHeroState((prev) => {
      if (prev.queue.length === 0) return prev;
      const [incoming, ...rest] = prev.queue;
      return {
        indices: prev.indices.map((v, i) => (i === slotIdx ? incoming : v)),
        queue: [...rest, prev.indices[slotIdx]],
      };
    });
  }, []);

  const s = (
    slotIdx: number,
    props: Omit<
      ComponentProps<typeof ShuffleImage>,
      'images' | 'imageIdx' | 'onCycle'
    >,
  ) => (
    <ShuffleImage
      images={images}
      imageIdx={heroState.indices[slotIdx]}
      onCycle={() => cycleSlot(slotIdx)}
      {...props}
    />
  );

  return (
    <div className="pointer-events-none absolute inset-0 flex -translate-y-10 gap-2 [maskImage:linear-gradient(0deg,rgba(0,0,0,0)_25%,rgba(0,0,0,1)_75%)] md:-translate-y-14">
      <div className="flex w-full flex-col gap-2">
        <div className="relative aspect-[2/1] overflow-hidden rounded-md">
          {s(0, { delay: 0, interval: 18000, zoom: 'in' })}
        </div>
        <div className="relative aspect-[1/1] overflow-hidden rounded-md">
          {s(1, { delay: 3400, interval: 15200 })}
        </div>
        <div className="relative aspect-[2/3] overflow-hidden rounded-md">
          {s(2, { delay: 1800, interval: 20800, zoom: 'out' })}
        </div>
      </div>
      <div className="flex w-full flex-col gap-2">
        <div className="relative aspect-[4/3] overflow-hidden rounded-md">
          {s(3, { delay: 4800, interval: 16400 })}
        </div>
        <div className="relative aspect-[1/1] overflow-hidden rounded-md">
          {s(4, { delay: 1000, interval: 14400, zoom: 'in' })}
        </div>
        <div className="relative aspect-[3/4] overflow-hidden rounded-md">
          {s(5, { delay: 6400, interval: 19200 })}
        </div>
      </div>
      <div className="flex w-full flex-col gap-2 max-md:hidden">
        <div className="relative aspect-[1/1] overflow-hidden rounded-md">
          {s(6, { delay: 2200, interval: 17200 })}
        </div>
        <div className="relative aspect-[3/4] overflow-hidden rounded-md">
          {s(7, { delay: 5800, interval: 14800, zoom: 'out' })}
        </div>
        <div className="relative aspect-[1/1] overflow-hidden rounded-md">
          {s(8, { delay: 800, interval: 20000 })}
        </div>
      </div>
      <div className="flex w-full flex-col gap-2 max-lg:hidden">
        <div className="relative aspect-[4/3] overflow-hidden rounded-md">
          {s(9, { delay: 3800, interval: 16800, zoom: 'in' })}
        </div>
        <div className="relative aspect-[1/1] overflow-hidden rounded-md">
          {s(10, { delay: 7000, interval: 15600 })}
        </div>
        <div className="relative aspect-[1/1] overflow-hidden rounded-md">
          {s(11, { delay: 1600, interval: 18800 })}
        </div>
        <div className="relative aspect-[2/1] overflow-hidden rounded-md">
          {s(12, { delay: 5200, interval: 20400, zoom: 'out' })}
        </div>
      </div>
    </div>
  );
};

const ArtistsSection = forwardRef<
  HTMLDivElement,
  {
    title: string;
    pages: EstatesStoryblok['section_4_artist_pages'];
    augmentedArtists: AugmentedArtistCard[];
  }
>(({ augmentedArtists, pages, title }, ref) => {
  const { t } = useTranslation();

  return (
    <PageSection ref={ref} className="scroll-mt-24">
      <section className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
        <h2 className="col-span-full w-full text-balance text-md text-neutral-4 md:text-lg">
          {title}
        </h2>

        {pages.map((page) => {
          if (typeof page === 'string') return null;

          const augmented = augmentedArtists.find((a) => a.slug === page.slug);
          const bannerImage =
            page.content.bannerImage?.filename ||
            page.content.thumbnail?.filename;

          return (
            <Tile className="group" direction="column">
              <TileMedia>
                {bannerImage ? (
                  <Image
                    alt={`Image for: ${
                      page.content.estateName || page.content.name
                    }`}
                    src={bannerImage}
                    hoverEffect
                    size={{
                      sm: { span: 12, ratio: '4/3' },
                      md: { span: 4, ratio: '4/3' },
                      lg: { span: 3, ratio: '4/3' },
                    }}
                  />
                ) : null}
              </TileMedia>

              <TileContent className="w-full gap-3" direction="row">
                {page.content.thumbnail ? (
                  <TileMedia className="size-12 shrink-0">
                    <Image
                      alt={
                        page.content.thumbnail.alt ||
                        `Image for: ${page.content.name}`
                      }
                      src={page.content.thumbnail.filename}
                      width={128}
                      hoverEffect
                    />
                  </TileMedia>
                ) : null}
                <TileContent className="w-full" direction="column">
                  <TileTitle>
                    <Link
                      href={`/${page.full_slug}`}
                      className="after:absolute after:inset-0 after:block after:content-['']"
                    >
                      {page.content.estateName || page.content.name}
                    </Link>
                  </TileTitle>
                  <TileText>
                    {getArtistTileOverline(
                      augmented?.totalWorks ?? 0,
                      page.content.hideCollaborations || false,
                      t,
                    )}
                  </TileText>
                </TileContent>
              </TileContent>
            </Tile>
          );
        })}
      </section>
    </PageSection>
  );
});

const FeaturedReleaseSection = ({
  pages,
  title,
}: {
  title: string;
  pages: EstatesStoryblok['section_1_release_pages'];
}) => {
  return (
    <PageSection>
      <section className="flex flex-col">
        <h2 className="w-full text-balance text-md text-neutral-4 md:border-b md:border-divider/20 md:pb-5 md:text-lg">
          {title}
        </h2>

        <div className="flex w-full flex-col divide-y divide-divider/20">
          {pages.map((page, index) => {
            if (typeof page === 'string') return null;

            return (
              <Link
                key={page.id}
                href={`/${page.full_slug}`}
                className="group flex w-full gap-3 py-5 transition duration-300 ease-out hover:text-neutral-4 max-md:flex-col-reverse"
              >
                <span className="flex w-full flex-col items-start gap-6">
                  {index === 0 && page.content.releaseStatus ? (
                    <Pill
                      isActive={page.content.releaseStatus === 'live'}
                      label={page.content.releaseStatus}
                    />
                  ) : null}
                  <span className="flex w-full flex-col">
                    <h2
                      className="w-full max-w-2xl text-balance font-bold"
                      style={STYLE_H2}
                    >
                      {page.content.title}
                    </h2>
                    {page.content.subtitle ? (
                      <h3
                        className="w-full max-w-2xl text-balance"
                        style={STYLE_H2}
                      >
                        {page.content.subtitle}
                      </h3>
                    ) : null}
                  </span>
                  {index === 0 && page.content.description ? (
                    <p className="line-clamp-3 max-w-2xl">
                      {page.content.description}
                    </p>
                  ) : null}
                </span>
                <span className="flex w-full flex-col gap-3 md:items-end">
                  <Image
                    alt={`Image for ${page.content.title}`}
                    src={page.content.thumbnail.filename}
                    size={{
                      sm: { span: 12, ratio: index === 0 ? '3/2' : '1/1' },
                      md: { span: 4, ratio: index === 0 ? '3/2' : '1/1' },
                      lg: { span: 4, ratio: index === 0 ? '3/2' : '1/1' },
                    }}
                    hoverEffect
                    className={classNames(
                      'w-full rounded-sm',
                      index === 0 ? 'md:max-w-xl' : 'max-w-24 md:max-w-30',
                    )}
                  />
                </span>
              </Link>
            );
          })}
        </div>
      </section>
    </PageSection>
  );
};

const VideosSection = ({
  title,
  videos,
}: {
  title: string;
  videos: VideoStoryblok[];
}) => {
  const lightboxItems = useMemo(
    () => videos.map(storyblokMediaItemToLightboxEntry),
    [videos],
  );

  return (
    <PageSection fullWidth>
      <Lightbox items={lightboxItems}>
        {(lightbox) => (
          <div>
            <Carousel
              arrowPosition="top"
              headline={title}
              items={videos.map((item, i) => {
                return (
                  <VideoCard
                    key={item.vimeo.vimeo_raw}
                    coverImage={item.coverImage}
                    onClick={() => {
                      lightbox.openOnSlide(i);
                    }}
                    video={item.vimeo}
                  />
                );
              })}
              perPage={2}
            />
          </div>
        )}
      </Lightbox>
    </PageSection>
  );
};

const ArtistsArtistSection = ({
  pages,
  title,
}: {
  title: string;
  pages: EstatesStoryblok['section_5_insight_pages'];
}) => {
  const [emitter] = useAnalyticsEmitter();

  return (
    <PageSection fullWidth>
      <Carousel
        arrowPosition="top"
        headline={title}
        items={pages.map((page, index) => {
          if (typeof page === 'string') return null;

          return (
            <JournalsTile
              key={page.id}
              description={page.content.subtitle}
              href={`/${page.full_slug}`}
              image={page.content.poster}
              imageRatio="3/4"
              mediaDuration={page.content.mediaDuration}
              onClick={() => {
                emitter('', 'article click', {
                  position: index + 1,
                  'article slug': page.full_slug,
                  'article tag': page.content.tag,
                  'article title': page.content.title,
                });
              }}
              publishedAt={page.content.date}
              readingTime={
                page.content.article
                  ? readingTimeCalculator(getWordCount(page.content.article))
                  : 0
              }
              tag={page.content.tag}
              title={page.content.title}
            />
          );
        })}
        perPage={4}
      />
    </PageSection>
  );
};

const QuoteSection = ({
  author,
  image,
  quote,
}: {
  image: AssetStoryblok;
  quote: string;
  author: string;
}) => {
  return (
    <PageSection>
      <section className="flex flex-col items-center justify-center gap-6 py-12">
        <div
          aria-hidden
          className="size-20 self-center overflow-hidden rounded-full"
        >
          <Image alt={`Image for ${author}`} src={image.filename} width={128} />
        </div>

        <div className="flex w-full flex-col gap-3">
          <p
            className="mx-auto w-full max-w-2xl text-balance text-center"
            style={STYLE_QUOTE}
          >
            {quote}
          </p>
          <p className="mx-auto w-full max-w-xs text-balance text-center text-neutral-4">
            {author}
          </p>
        </div>
      </section>
    </PageSection>
  );
};

export const Estates = ({
  augmentedArtists,
  story,
}: {
  story: EstatesStoryblok;
  augmentedArtists: AugmentedArtistCard[];
}) => {
  const artistsSectionRef = useRef<HTMLDivElement>(null);

  return (
    <main className="md:gap-15 my-9 flex flex-col gap-12 md:my-12">
      <header>
        <PageSection>
          <section className="relative flex flex-col items-center justify-center gap-5 pb-12 pt-48 sm:pt-72 md:pb-24 lg:pt-96">
            <HeroFloatingImages images={story.header_images} />
            <h1
              className="relative mx-auto w-full max-w-2xl text-balance text-center font-bold"
              style={STYLE_H1}
            >
              {story.header_title}
            </h1>
            <p className="relative mx-auto w-full max-w-md text-balance text-center">
              {story.header_body}
            </p>

            <Button
              label={story.header_button}
              size="lg"
              onClick={() => {
                artistsSectionRef.current?.scrollIntoView({
                  behavior: 'smooth',
                });
              }}
            />
          </section>
        </PageSection>
      </header>

      <FeaturedReleaseSection
        title={story.section_1_heading}
        pages={story.section_1_release_pages}
      />

      <VideosSection
        title={story.section_2_heading}
        videos={story.section_2_videos}
      />

      <QuoteSection
        image={story.section_3_image}
        author={story.section_3_author}
        quote={story.section_3_quote}
      />

      <ArtistsSection
        title={story.section_4_heading}
        ref={artistsSectionRef}
        pages={story.section_4_artist_pages}
        augmentedArtists={augmentedArtists}
      />

      <ArtistsArtistSection
        title={story.section_5_heading}
        pages={story.section_5_insight_pages}
      />

      <PageSection>
        <SubscriptionBanner
          buttonLocation="estates index"
          image={story.subscription_image.filename}
          source={AuthEntryPoint.NewsletterEstates}
          subtitle={story.subscription_description}
          title={story.subscription_title}
        />
      </PageSection>
    </main>
  );
};
